import React, { useState } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import classNames from 'classnames';
import includes from 'lodash/includes';
import { Link } from 'react-router-dom';
import { FormattedMessage, FormattedDate } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import {
  faWindows as fabFaWindows,
  faApple as fabFaApple,
  faLinux as fabFaLinux,
} from '@fortawesome/free-brands-svg-icons';

import { supportUrl } from 'Config/lang';
import MysteryProductDetails from 'Components/Order/MysteryProductDetails/MysteryProductDetails';
import Drm from 'Components/Utility/Drm';
import EpicActivate from 'Components/Order/EpicActivate';
import VaultNActivate from 'Components/Order/VaultnActivate';
import KeyLibraryCardReview from 'Components/ProductReviews/OrderItemReviews/KeyLibraryCardReview';
import KeyReveal from 'Components/Order/KeyReveal';
import KeyReveals from 'Helpers/Order/KeyReveals';
import OrderItemPreorderMessage from 'Components/Order/OrderItemPreorderMessage';
import ProductCover from 'Components/Utility/ProductCover';
import RedeemSupport from 'Components/Order/RedeemSupport';
import useViewport from 'Hooks/useViewport';
import MysteryReveal from 'Components/Order/KeyReveals/MysteryReveal';
import LazyModal from 'Components/Utility/LazyModal';

import 'Components/Order/OrderItem.scss';

const itemLevelMysteryReveals = ['present', 'scratchcard', 'envelope', 'wheel', 'wheel alt', 'pinata', 'bonus drop'];

export default function OrderItem({
  game: rawGame,
  orderID,
  bundleID,
  bundleItem,
  order,
  emailConfirmed,
  showKeyLibraryExtras,
  currentOpenProductDetails,
  setCurrentOpenProductDetails,
}) {
  const [name, setName] = useState(rawGame.name);
  const [mysteryGameDetails, setMysteryGameDetails] = useState(rawGame.mysteryGameDetails);
  const [mysteryRevealed, setMysteryRevealed] = useState(!!rawGame.mysteryRevealed);
  const [key, setKey] = useState(rawGame.key);
  const [skipAnimation, setSkipAnimation] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  const { isAboveSm, isBelowMd } = useViewport();

  const game = { ...rawGame, key, name, mysteryRevealed, mysteryGameDetails };

  const isGiftOrder = !isEmpty(get(order, 'gift_order'));
  const orderStatus = order.status;
  const orderPaymentStatus = get(order, 'payment.status', null);

  let drmPlatform = '';

  // If key is revealed don't use reveal template
  let revealTemplate = (game.key || mysteryRevealed ? null : game.reveal_template) || 'store';
  if (game.reveal_template && !itemLevelMysteryReveals.includes(game.reveal_template)) revealTemplate = 'store';

  if (game.drm) {
    drmPlatform = Object.keys(game.drm).filter(p => game.drm[p])[0];
  }

  if (mysteryGameDetails && mysteryGameDetails.drm) {
    drmPlatform = Object.keys(mysteryGameDetails.drm).filter(p => mysteryGameDetails.drm[p])[0];
  }

  const ubisoftSkaBundleItem = (bundleItem && bundleItem.type === 'bundle' && bundleItem.ubisoft_ska) ? bundleItem : {};

  if (game.ubisoft_ska) {
    revealTemplate = 'ubisoft';

    if (
      !game.ubisoft_redeemed &&
      game.genbaId !== ubisoftSkaBundleItem.genbaId &&
      !isEmpty(ubisoftSkaBundleItem)
    ) {
      return false;
    }

    // If its a genba ubisoft ska bundle then display the bundle name
    if (get(ubisoftSkaBundleItem, '_id', '') !== '' && !game.ubisoft_redeemed) {
      game.name = ubisoftSkaBundleItem.name; // eslint-disable-line no-param-reassign
    }
  }

  if (game.origin_ska) {
    revealTemplate = 'origin';
  }

  if (game.epic_ska) {
    revealTemplate = 'epic';
  }

  if (game.vaultnKeyless) {
    revealTemplate = 'vaultnKeyless';
  }

  if (includes(game.name.toLowerCase(), 'voucher:') || includes(game.name.toLowerCase(), 'coupon:') || includes(game.name.toLowerCase(), 'spending spree') || game.type === 'voucher') {
    drmPlatform = 'coupon';
  }
  if (includes(game.name.toLowerCase(), 'redeem:') || includes(game.name.toLowerCase(), 'redeem code:')) {
    drmPlatform = 'redeem';
  }
  if (includes(game.name.toLowerCase(), 'bethesda:')) {
    drmPlatform = 'bethesda';
  }

  if (orderStatus !== 'MANUAL_AUTHORIZATION' && !skipAnimation && itemLevelMysteryReveals.includes(revealTemplate)) {
    if (!emailConfirmed && orderPaymentStatus !== 'REDEEM_CODE') {
      // Hide key reveals until email is confirmed
      return false;
    }

    return (
      <MysteryReveal
        bundleID={bundleID}
        item={game}
        mysteryGameDetails={mysteryGameDetails}
        orderID={orderID}
        setMysteryGameDetails={setMysteryGameDetails}
        setMysteryRevealed={setMysteryRevealed}
        setName={setName}
        skipFunction={setSkipAnimation}
      />
    );
  }

  const canReveal =
    game.status !== 'refunded' &&
    game.status !== 'frauded' &&
    !game.preorder_pending &&
    !game.preorderKeyTime &&
    !(game.preorder_date && game.ztormId) &&
    orderStatus !== 'MANUAL_AUTHORIZATION' &&
    orderStatus !== 'REFUNDED';

  const removed = ['refunded', 'frauded'].includes(game.status) && orderStatus !== 'MANUAL_AUTHORIZATION';

  const manualAuth = orderStatus === 'MANUAL_AUTHORIZATION' && !removed;

  const strippedName = KeyReveals.formatProductName(name);

  const freeOrder = !order.payment || order.payment.total === 0;

  const onProductDetailsOpen = (modal) => {
    if (currentOpenProductDetails === game.iid) {
      setCurrentOpenProductDetails(null);
      setDetailsModalOpen(false);
    } else if (modal) {
      setCurrentOpenProductDetails(game.iid);
      setDetailsModalOpen(true);
    } else {
      setCurrentOpenProductDetails(game.iid);
      setTimeout(() => {
        document.getElementById(game.iid).scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const isRevealed = !!game.key || mysteryRevealed;

  let cover = game.cover;

  if (get(mysteryGameDetails, 'cover') && isRevealed) {
    cover = mysteryGameDetails.cover;
  }

  return (
    <React.Fragment>
      <article
        id={game.iid}
        className={classNames(
          'new-order-item',
          { 'bundle-order-item': bundleID !== null },
          { 'comic-order-item': game.type === 'comic' || game.type === 'book' || game.type === 'audio' || game.type === 'video' },
          { 'software-order-item': game.display_type === 'software' },
        )}
      >
        {isAboveSm &&
          <ProductCover
            thumbnail
            cover={cover}
            alt={`Product cover for ${strippedName}`}
            displayType={game.display_type}
          />
        }
        <div className="order-item-details-container">
          <div className="name-icons-container">
            <h4 className="game-name">
              {game.isFreeProduct &&
                <FormattedMessage id="order-item.isFreeProduct" defaultMessage="BONUS:" />
              } {strippedName}
            </h4>
            {showKeyLibraryExtras &&
              <div className="additional-info">
                {game.bundleName &&
                  <FormattedMessage id="order-item.bundle" defaultMessage="Bundle: {bundleName}" values={{ bundleName: game.bundleName }} />
                }
                <Link to={`/orders/${order._id}`}>
                  {isGiftOrder ?
                    <FormattedMessage
                      id="order-item.redeemed"
                      defaultMessage="Redeemed: {purchased}"
                      values={{ purchased: <FormattedDate value={game.purchased} year="numeric" month="long" day="2-digit" /> }}
                    />
                    :
                    <FormattedMessage
                      id="order-item.purchased"
                      defaultMessage="Purchased: {purchased}"
                      values={{ purchased: <FormattedDate value={game.purchased} year="numeric" month="long" day="2-digit" /> }}
                    />
                  }
                </Link>
              </div>
            }
            {(game.type === 'game' || game.type === 'dlc' || game.type === 'gift-card') &&
              <div className="icons-container">
                {drmPlatform && drmPlatform !== 'drm_free' &&
                  <Drm drmType={drmPlatform} />
                }
                {game.platforms &&
                  <div>
                    {game.platforms.windows &&
                      <FontAwesomeIcon icon={fabFaWindows} className="mr-2" />
                    }
                    {game.platforms.mac &&
                      <FontAwesomeIcon icon={fabFaApple} className="mr-2" />
                    }
                    {game.platforms.linux &&
                      <FontAwesomeIcon icon={fabFaLinux} className="mr-2" />
                    }
                  </div>
                }
              </div>
            }
            {!isEmpty(mysteryGameDetails) && isRevealed && isAboveSm &&
              <button
                className="product-details-button btn-reset-style"
                onClick={onProductDetailsOpen}
              >
                <FormattedMessage
                  id="order-item.wheel-spin.seeproductdetails"
                  defaultMessage="See product details"
                />
                <span className="ml-1">
                  {game._id === currentOpenProductDetails ?
                    <FontAwesomeIcon icon={faChevronDown} />
                    :
                    <FontAwesomeIcon icon={faChevronRight} />
                  }
                </span>

              </button>
            }
            {!isEmpty(mysteryGameDetails) && isRevealed && isBelowMd &&
              <button
                className="product-details-button btn-reset-style"
                onClick={() => { onProductDetailsOpen(true); }}
              >
                <FormattedMessage
                  id="order-item.wheel-spin.seeproductdetails"
                  defaultMessage="See product details"
                />
                <span className="ml-1">
                  {game._id === currentOpenProductDetails ?
                    <FontAwesomeIcon icon={faChevronDown} />
                    :
                    <FontAwesomeIcon icon={faChevronRight} />
                  }
                </span>

              </button>
            }
          </div>

          <div className="key-container">
            {drmPlatform === 'coupon' &&
              <div className="key-deadline-text">
                <FormattedMessage
                  id="order-item.wheel-spin.smalltext.coupon"
                  defaultMessage="Only one coupon can be applied per order. Multiple coupons will not stack."
                />
              </div>
            }

            {revealTemplate === 'store' && canReveal &&
              <KeyReveal
                game={game}
                orderStatus={orderStatus}
                orderID={orderID}
                bundleID={bundleID}
                drm={drmPlatform}
                emailConfirmed={emailConfirmed}
                setName={setName}
                setKey={setKey}
                setMysteryGameDetails={setMysteryGameDetails}
                freeOrder={freeOrder}
                setMysteryRevealed={setMysteryRevealed}
                mysteryRevealed={mysteryRevealed}
              />
            }

            {revealTemplate === 'ubisoft' && canReveal &&
              <div className="btn btn-secondary btn-block disabled">
                <FormattedMessage id="order-item.activation" defaultMessage="Activation Expired" />
              </div>
            }

            {revealTemplate === 'origin' && canReveal &&
              <div className="btn btn-secondary btn-block disabled">
                <FormattedMessage id="order-item.activation" defaultMessage="Activation Expired" />
              </div>
            }

            {revealTemplate === 'epic' && canReveal &&
              <EpicActivate
                game={game}
                orderStatus={orderStatus}
                oid={orderID}
                bid={bundleID}
                emailConfirmed={emailConfirmed}
              />
            }

            {revealTemplate === 'vaultnKeyless' && canReveal &&
              <VaultNActivate
                game={game}
                orderStatus={orderStatus}
                oid={orderID}
                bid={bundleID}
                emailConfirmed={emailConfirmed}
              />
            }

            {(removed || orderStatus === 'REFUNDED') &&
              <div>
                <FormattedMessage
                  id="order-item.partialRefund"
                  defaultMessage="This item has been removed from your order."
                />
              </div>
            }

            {orderStatus === 'COMPLETE' && !removed &&
              <OrderItemPreorderMessage game={game} />
            }

            {manualAuth &&
              <div>
                <FormattedMessage
                  id="order-item.processing"
                  defaultMessage="Your order has been flagged for manual authorisation. You can find out more about this in the support article - "
                />
                <a href={`${supportUrl()}/articles/201027912-Order-Status-Manual-Authorisation-Why-do-I-have-them-`} target="_blank" rel="noopener noreferrer">
                  <FormattedMessage id="order-item.orderstatus" defaultMessage="Why is my order status Manual Authorisation?" />
                </a>
              </div>
            }

            {canReveal &&
              <div className="support-review-container">
                <RedeemSupport
                  epicSKA={game.epic_ska}
                  drmPlatform={drmPlatform}
                  ubisoftSKA={game.ubisoft_ska}
                  bundleID={bundleID}
                  supportLink={get(game, 'support_link', '')}
                  game={game}
                />
                {showKeyLibraryExtras &&
                  <KeyLibraryCardReview
                    item={game}
                    productId={game._id}
                    slug={game.slug}
                  />
                }
              </div>
            }
          </div>
        </div>
      </article>

      {game.order_smallprint &&
        <div className="order-item-smallprint">
          {strippedName}: {game.order_smallprint}
        </div>
      }
      {/* eslint-disable max-len */}
      {!isEmpty(mysteryGameDetails) && game.iid === currentOpenProductDetails && isRevealed && isAboveSm &&
        <MysteryProductDetails product={mysteryGameDetails} cover={cover} />
      }
      {!isEmpty(mysteryGameDetails) && game.iid === currentOpenProductDetails && isRevealed && isBelowMd &&
        <LazyModal
          open={detailsModalOpen}
          setOpen={onProductDetailsOpen}
          className="MysteryProductDetailsModal"
        >
          <MysteryProductDetails mobileLayout product={mysteryGameDetails} cover={cover} />
        </LazyModal>
      }
    </React.Fragment>
  );
}

OrderItem.propTypes = {
  game: PropTypes.shape({}),
  orderID: PropTypes.string,
  bundleID: PropTypes.string,
  order: PropTypes.shape({}).isRequired,
  bundleItem: PropTypes.shape({}),
  showKeyLibraryExtras: PropTypes.bool,
  emailConfirmed: PropTypes.bool,
};

OrderItem.defaultProps = {
  game: {},
  orderID: '',
  bundleID: null,
  bundleItem: {},
  showKeyLibraryExtras: false,
  emailConfirmed: false,
};
